/**
 * The sidebar's map: the menu of the v1 admin panel that staff know, entry
 * for entry and in its order, each pointing at the screen of this app that
 * does the same job. Four entries stand on their own at the top; the rest
 * sit in v1's five groups. The top bar reads off it where a page is.
 *
 * An entry whose v1 page has no screen here yet still stands in its place
 * and opens `/soon/<entry>`, which says so: the menu is whole from the first
 * day, and an entry is wired to its screen when the screen exists.
 *
 * An entry is shown to the roles of its section (`shared/auth/access.ts`);
 * `need` narrows it further. A screen of this app that v1 has no entry for
 * sits under the entry closest to it (`items`).
 */
import type { Section } from "@/shared/auth/access";
import type { IconName } from "@/shared/ui/Icon";

/** Who an entry or a screen is shown to, beyond whoever its section opens to. */
export type RailNeed =
  | "staff"
  | "settings"
  | "superadmin"
  | "account_access"
  | "account_administration";

export interface RailItem {
  id: string;
  to: RailPath;
  /** A translation key. */
  labelKey: string;
  /** Other addresses that are this screen's too: a wallet reached through its specialist. */
  under?: readonly string[];
  need?: RailNeed;
  /**
   * The screen is its entry's plain list: a page under it is said by the
   * entry alone ("Clients / Dilnoza"), not by the list as well.
   */
  plain?: boolean;
}

/** v1's groups, in its order. The first entries belong to none. */
export type RailGroupId = "people" | "catalog" | "content" | "places" | "system";

export const RAIL_GROUPS: Readonly<Record<RailGroupId, string>> = {
  people: "nav.groups.people",
  catalog: "nav.groups.catalog",
  content: "nav.groups.content",
  places: "nav.groups.places",
  system: "nav.groups.system",
};

export interface RailEntry {
  id: RailEntryId;
  /** The section whose roles open it. */
  section: Section;
  group?: RailGroupId;
  to: RailPath;
  /** Part of the address: entries that share a screen differ by it (the reference lists). */
  search?: Readonly<Record<string, string>>;
  icon: IconName;
  /** A translation key. */
  labelKey: string;
  need?: RailNeed;
  /** Addresses under `to` that belong to another entry. */
  except?: readonly string[];
  items: readonly RailItem[];
  /** For an entry not built here yet: its page in the v1 panel, from `/admin`. */
  v1?: string;
}

/** v1's 41 menu entries. */
export type RailEntryId =
  | "home"
  | "dispatch"
  | "orders"
  | "support"
  | "specialists"
  | "commission"
  | "payouts"
  | "click_pilot"
  | "map"
  | "clients"
  | "balance"
  | "debts"
  | "referrals"
  | "staff"
  | "services"
  | "specializations"
  | "age_groups"
  | "relationships"
  | "rating_tags"
  | "low_rating_reasons"
  | "cancel_reasons"
  | "rating_moderation"
  | "news"
  | "news_tags"
  | "stories"
  | "promo_codes"
  | "legal"
  | "regions"
  | "districts"
  | "massives"
  | "statistics"
  | "notifications"
  | "notification_log"
  | "sms_log"
  | "audit"
  | "blacklist"
  | "payments"
  | "deactivated"
  | "settings"
  | "firebase"
  | "tracking";

/** Every address the sidebar leads to: each is a route. */
export type RailPath =
  | "/now"
  | "/orders"
  | "/orders/map"
  | "/orders/course-checks"
  | "/orders/new"
  | "/specialists"
  | "/specialists/ratings"
  | "/specialists/blocked"
  | "/specialists/support"
  | "/clients"
  | "/clients/blocks"
  | "/money"
  | "/money/wallets"
  | "/money/topups"
  | "/money/payouts"
  | "/money/adjustments"
  | "/money/ledger"
  | "/money/report"
  | "/money/bank-calls"
  | "/money/channels"
  | "/catalog/services"
  | "/catalog/prices"
  | "/catalog/professions"
  | "/catalog/lists"
  | "/catalog/areas"
  | "/growth/promo-codes"
  | "/growth/partners"
  | "/growth/news"
  | "/growth/stories"
  | "/growth/campaigns"
  | "/growth/messages"
  | "/growth/legal"
  | "/settings"
  | "/settings/staff"
  | "/settings/markets"
  | "/settings/dispatch"
  | "/settings/stuck-visits"
  | "/settings/course-prices"
  | "/settings/course-bonus"
  | "/settings/partners"
  | "/settings/partner-deliveries"
  | "/settings/apps"
  | "/settings/audit"
  | "/settings/actions"
  | "/settings/account"
  | "/soon/$entry";

/** The address of an entry v1 has and this app has no screen for yet. */
export const SOON = "/soon/$entry" satisfies RailPath;

const soon = (
  id: RailEntryId,
  section: Section,
  group: RailGroupId,
  icon: IconName,
  v1: string,
): RailEntry => ({
  id,
  section,
  group,
  to: SOON,
  icon,
  labelKey: `nav.v1.${id}`,
  items: [],
  v1,
});

export const RAIL: readonly RailEntry[] = [
  // ─── v1's first four ────────────────────────────────────────────────
  { id: "home", section: "now", to: "/now", icon: "home", labelKey: "nav.v1.home", items: [] },
  {
    id: "dispatch",
    section: "orders",
    to: "/orders/new",
    icon: "phone",
    labelKey: "nav.v1.dispatch",
    items: [],
  },
  {
    id: "orders",
    section: "orders",
    to: "/orders",
    icon: "orders",
    labelKey: "nav.v1.orders",
    except: ["/orders/map", "/orders/new"],
    items: [
      { id: "list", to: "/orders", labelKey: "nav.v1.orders", plain: true },
      { id: "course_checks", to: "/orders/course-checks", labelKey: "nav.orders.course_checks" },
    ],
  },
  {
    id: "support",
    section: "specialists",
    to: "/specialists/support",
    icon: "support",
    labelKey: "nav.v1.support",
    items: [],
  },

  // ─── People ─────────────────────────────────────────────────────────
  {
    id: "specialists",
    section: "specialists",
    group: "people",
    to: "/specialists",
    icon: "specialists",
    labelKey: "nav.v1.specialists",
    except: ["/specialists/support", "/specialists/ratings", "/specialists/blocked"],
    items: [],
  },
  {
    id: "commission",
    section: "catalog",
    group: "people",
    to: "/catalog/prices",
    search: { view: "commission" },
    icon: "percent",
    labelKey: "nav.v1.commission",
    items: [],
  },
  {
    id: "payouts",
    section: "money",
    group: "people",
    to: "/money/payouts",
    icon: "payout",
    labelKey: "nav.v1.payouts",
    items: [],
  },
  soon("click_pilot", "money", "people", "chart", "/click-pilot"),
  {
    id: "map",
    section: "orders",
    group: "people",
    to: "/orders/map",
    icon: "map",
    labelKey: "nav.v1.map",
    items: [],
  },
  {
    id: "clients",
    section: "clients",
    group: "people",
    to: "/clients",
    icon: "clients",
    labelKey: "nav.v1.clients",
    except: ["/clients/blocks"],
    items: [],
  },
  {
    // v1's Balance was every balance transaction: here, the ledger. The rest of the money
    // screens v1 has no entry for sit under it.
    id: "balance",
    section: "money",
    group: "people",
    to: "/money/ledger",
    icon: "coins",
    labelKey: "nav.v1.balance",
    items: [
      { id: "ledger", to: "/money/ledger", labelKey: "money.nav.ledger" },
      { id: "home", to: "/money", labelKey: "money.nav.home" },
      {
        id: "wallets",
        to: "/money/wallets",
        labelKey: "money.nav.wallets",
        under: ["/money/specialists"],
      },
      { id: "adjustments", to: "/money/adjustments", labelKey: "money.nav.adjustments" },
      { id: "report", to: "/money/report", labelKey: "money.nav.report" },
      // Every call to the bank: hg-billing gives it to a superadmin alone.
      {
        id: "bank_calls",
        to: "/money/bank-calls",
        labelKey: "money.nav.bank_calls",
        need: "superadmin",
      },
      { id: "channels", to: "/money/channels", labelKey: "money.nav.channels", need: "settings" },
    ],
  },
  {
    id: "debts",
    section: "money",
    group: "people",
    to: "/money/wallets",
    search: { state: "in_debt" },
    icon: "debt",
    labelKey: "nav.v1.debts",
    items: [],
  },
  {
    id: "referrals",
    section: "growth",
    group: "people",
    to: "/growth/partners",
    icon: "link",
    labelKey: "nav.v1.referrals",
    items: [],
  },
  {
    id: "staff",
    section: "settings",
    group: "people",
    to: "/settings/staff",
    icon: "staff",
    labelKey: "nav.v1.staff",
    need: "staff",
    items: [],
  },

  // ─── Catalog ────────────────────────────────────────────────────────
  {
    id: "services",
    section: "catalog",
    group: "catalog",
    to: "/catalog/services",
    icon: "services",
    labelKey: "nav.v1.services",
    items: [
      { id: "services", to: "/catalog/services", labelKey: "catalog.nav.services", plain: true },
      { id: "prices", to: "/catalog/prices", labelKey: "catalog.nav.prices" },
    ],
  },
  {
    id: "specializations",
    section: "catalog",
    group: "catalog",
    to: "/catalog/professions",
    icon: "professions",
    labelKey: "nav.v1.specializations",
    items: [],
  },
  {
    id: "age_groups",
    section: "catalog",
    group: "catalog",
    to: "/catalog/lists",
    search: { list: "age_groups" },
    icon: "ageGroups",
    labelKey: "nav.v1.age_groups",
    items: [],
  },
  {
    id: "relationships",
    section: "catalog",
    group: "catalog",
    to: "/catalog/lists",
    search: { list: "relationships" },
    icon: "heart",
    labelKey: "nav.v1.relationships",
    items: [],
  },
  {
    id: "rating_tags",
    section: "catalog",
    group: "catalog",
    to: "/catalog/lists",
    search: { list: "rating_tags" },
    icon: "star",
    labelKey: "nav.v1.rating_tags",
    items: [],
  },
  // A low rating's reasons are the rating tags of the kind `complaint` in v2.
  {
    id: "low_rating_reasons",
    section: "catalog",
    group: "catalog",
    to: "/catalog/lists",
    search: { list: "rating_tags" },
    icon: "lowRating",
    labelKey: "nav.v1.low_rating_reasons",
    items: [],
  },
  {
    id: "cancel_reasons",
    section: "catalog",
    group: "catalog",
    to: "/catalog/lists",
    search: { list: "cancel_reasons" },
    icon: "cancelReasons",
    labelKey: "nav.v1.cancel_reasons",
    items: [],
  },
  {
    id: "rating_moderation",
    section: "specialists",
    group: "catalog",
    to: "/specialists/ratings",
    icon: "shield",
    labelKey: "nav.v1.rating_moderation",
    items: [],
  },

  // ─── Content ────────────────────────────────────────────────────────
  {
    id: "news",
    section: "growth",
    group: "content",
    to: "/growth/news",
    icon: "news",
    labelKey: "nav.v1.news",
    items: [],
  },
  // The news tags are a dialog on the news list.
  {
    id: "news_tags",
    section: "growth",
    group: "content",
    to: "/growth/news",
    icon: "tag",
    labelKey: "nav.v1.news_tags",
    items: [],
  },
  {
    id: "stories",
    section: "growth",
    group: "content",
    to: "/growth/stories",
    icon: "stories",
    labelKey: "nav.v1.stories",
    items: [],
  },
  {
    id: "promo_codes",
    section: "growth",
    group: "content",
    to: "/growth/promo-codes",
    icon: "ticket",
    labelKey: "nav.v1.promo_codes",
    items: [],
  },
  {
    id: "legal",
    section: "growth",
    group: "content",
    to: "/growth/legal",
    icon: "legal",
    labelKey: "nav.v1.legal",
    items: [],
  },

  // ─── Places ─────────────────────────────────────────────────────────
  {
    id: "regions",
    section: "catalog",
    group: "places",
    to: "/catalog/areas",
    icon: "pin",
    labelKey: "nav.v1.regions",
    items: [],
  },
  soon("districts", "catalog", "places", "districts", "/locations/districts"),
  soon("massives", "catalog", "places", "building", "/locations/massives"),

  // ─── System ─────────────────────────────────────────────────────────
  soon("statistics", "now", "system", "stats", "/statistics/orders"),
  {
    id: "notifications",
    section: "growth",
    group: "system",
    to: "/growth/campaigns",
    icon: "megaphone",
    labelKey: "nav.v1.notifications",
    items: [],
  },
  // hg-notify keeps one person's pushes (their page), and no list of everybody's yet.
  soon("notification_log", "growth", "system", "bell", "/notification-logs"),
  {
    id: "sms_log",
    section: "growth",
    group: "system",
    to: "/growth/messages",
    icon: "sms",
    labelKey: "nav.v1.sms_log",
    // It holds everybody's numbers: a superadmin's alone.
    need: "superadmin",
    items: [],
  },
  {
    id: "audit",
    section: "settings",
    group: "system",
    to: "/settings/actions",
    icon: "audit",
    labelKey: "nav.v1.audit",
    // What staff do, in every service: hg-audit gives it to a superadmin alone.
    need: "superadmin",
    items: [
      { id: "actions", to: "/settings/actions", labelKey: "nav.settings.actions" },
      { id: "audit_v1", to: "/settings/audit", labelKey: "nav.settings.audit" },
    ],
  },
  {
    id: "blacklist",
    section: "clients",
    group: "system",
    to: "/clients/blocks",
    icon: "ban",
    labelKey: "nav.v1.blacklist",
    need: "account_administration",
    items: [],
  },
  {
    id: "payments",
    section: "money",
    group: "system",
    to: "/money/topups",
    icon: "card",
    labelKey: "nav.v1.payments",
    items: [],
  },
  {
    id: "deactivated",
    section: "specialists",
    group: "system",
    to: "/specialists/blocked",
    icon: "userOff",
    labelKey: "nav.v1.deactivated",
    need: "account_access",
    items: [],
  },
  {
    id: "settings",
    section: "settings",
    group: "system",
    to: "/settings",
    icon: "gear",
    labelKey: "nav.v1.settings",
    except: ["/settings/staff", "/settings/actions", "/settings/audit"],
    items: [
      { id: "markets", to: "/settings/markets", labelKey: "nav.settings.markets" },
      { id: "dispatch", to: "/settings/dispatch", labelKey: "accounts.settings.dispatch" },
      { id: "stuck", to: "/settings/stuck-visits", labelKey: "nav.settings.stuck" },
      {
        id: "course_prices",
        to: "/settings/course-prices",
        labelKey: "nav.settings.course_prices",
      },
      { id: "course_bonus", to: "/settings/course-bonus", labelKey: "nav.settings.course_bonus" },
      { id: "apps", to: "/settings/apps", labelKey: "nav.settings.apps" },
      // An admin reads how the partners stand; switching one is a superadmin's, on the page.
      { id: "partners", to: "/settings/partners", labelKey: "nav.settings.partners" },
      {
        id: "deliveries",
        to: "/settings/partner-deliveries",
        labelKey: "nav.settings.deliveries",
      },
      { id: "account", to: "/settings/account", labelKey: "accounts.settings.account" },
    ],
  },
  soon("firebase", "settings", "system", "flame", "/settings/firebase"),
  soon("tracking", "orders", "system", "route", "/tracking"),
];

/** Whether an address is `path` itself or a page under it. */
function under(pathname: string, path: string): boolean {
  return pathname === path || pathname.startsWith(`${path}/`);
}

export interface Place {
  entry: RailEntry;
  /** The screen of the entry the address is on; null for an entry with none. */
  item: RailItem | null;
}

/**
 * Where an address is in the sidebar: its entry, and the screen of it.
 *
 * The entry is the one whose address reaches furthest into this one. Entries
 * that share a screen (the reference lists) are told apart by the address's
 * search: the one whose `search` it holds, else the one with none. A
 * `/soon/<entry>` address is that entry's. The screen is the one whose
 * address reaches furthest; an entry's first screen is where everything else
 * under the entry belongs (an order's page is the list's).
 */
export function placeOf(
  pathname: string,
  search: Readonly<Record<string, unknown>> = {},
  entries: readonly RailEntry[] = RAIL,
): Place | null {
  const waiting = /^\/soon\/([^/]+)$/.exec(pathname)?.[1];
  if (waiting !== undefined) {
    const entry = entries.find((each) => each.to === SOON && each.id === waiting);
    return entry === undefined ? null : { entry, item: null };
  }

  // How far an entry reaches into the address: through its own address, or a screen of it
  // that lives elsewhere (the price desk is the services' screen).
  const reach = (each: RailEntry) =>
    Math.max(
      -1,
      ...[each.to, ...each.items.flatMap((item) => [item.to, ...(item.under ?? [])])]
        .filter((path) => under(pathname, path))
        .map((path) => path.length),
    );
  const candidates = entries.filter(
    (each) =>
      each.to !== SOON &&
      reach(each) >= 0 &&
      each.except?.some((path) => under(pathname, path)) !== true,
  );
  const longest = Math.max(-1, ...candidates.map(reach));
  const tied = candidates.filter((each) => reach(each) === longest);
  const holds = (each: RailEntry) =>
    each.search !== undefined &&
    Object.entries(each.search).every(([key, value]) => search[key] === value);
  // Entries that share a screen and none of whose searches this address holds: it is that
  // screen's own default (the lists' categories), which v1 has no entry for.
  const entry = tied.find(holds) ?? tied.find((each) => each.search === undefined);
  if (entry === undefined) return null;

  const depth = (item: RailItem) =>
    Math.max(
      -1,
      ...[item.to, ...(item.under ?? [])]
        .filter((path) => under(pathname, path))
        .map((path) => path.length),
    );
  const item =
    entry.items.filter((each) => depth(each) >= 0).sort((a, b) => depth(b) - depth(a))[0] ??
    entry.items.find((each) => each.to === entry.to) ??
    null;
  return { entry, item };
}
